<template>
  <div class="home-page">
    <!-- Banner轮播区域 -->
    <section class="hero-banner">
      <!-- 背景轮播 -->
      <el-carousel 
        class="banner-carousel"
        :interval="5000" 
        arrow="always"
        indicator-position="none"
        @change="onBannerChange"
        height="100vh"
      >
        <el-carousel-item v-for="(banner, index) in banners" :key="index">
          <div class="banner-slide" :style="{ backgroundImage: `url(${getImageUrl(banner.image)})` }">
            <div class="banner-overlay"></div>
          </div>
        </el-carousel-item>
      </el-carousel>

      <!-- 会议信息浮层 -->
      <div class="hero-content">
        <div class="content-wrapper">
          <div class="hero-left">
            <div class="hero-badge">2026年度盛会</div>
            <h1 class="hero-title">{{ conferenceInfo.title }}</h1>
            <p class="hero-subtitle">{{ conferenceInfo.subtitle }}</p>
            <p class="hero-desc">探索宇宙奥秘，共话天文前沿</p>
            <div class="hero-info">
              <div class="info-item">
                <i class="el-icon-date"></i>
                <span>{{ conferenceInfo.start_date }} - {{ conferenceInfo.end_date }}</span>
              </div>
              <div class="info-item">
                <i class="el-icon-location-outline"></i>
                <span>{{ conferenceInfo.address }}</span>
              </div>
            </div>
            <div class="hero-actions">
              <el-button type="primary" size="large" @click="goToRegister">立即报名</el-button>
              <el-button size="large" plain @click="goToAbout">了解更多</el-button>
            </div>
          </div>
          <div class="hero-right">
            <div class="countdown-card">
              <div class="countdown-title">大会倒计时</div>
              <div class="countdown-timer">
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdown.days }}</span>
                  <span class="countdown-unit">天</span>
                </div>
                <div class="countdown-sep">:</div>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdown.hours }}</span>
                  <span class="countdown-unit">时</span>
                </div>
                <div class="countdown-sep">:</div>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdown.minutes }}</span>
                  <span class="countdown-unit">分</span>
                </div>
                <div class="countdown-sep">:</div>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdown.seconds }}</span>
                  <span class="countdown-unit">秒</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 轮播指示器 -->
      <div class="banner-indicators">
        <span 
          v-for="(banner, index) in banners" 
          :key="index"
          class="indicator-dot"
          :class="{ active: currentBannerIndex === index }"
        ></span>
      </div>

      <!-- 向下滚动提示 -->
      <div class="hero-scroll-hint">
        <span>向下滚动</span>
        <i class="el-icon-arrow-down"></i>
      </div>
    </section>

    <!-- 会议主题 -->
    <section class="section theme-section">
      <div class="container">
        <h2 class="section-title">大会主题</h2>
        <p class="section-subtitle">基于10米级光学红外望远镜的天文学前沿探索</p>
        <div class="theme-grid">
          <div v-for="(theme, index) in themes" :key="index" class="theme-card">
            <div class="theme-icon">
              <i :class="theme.icon"></i>
            </div>
            <h3 class="theme-title">{{ theme.title }}</h3>
            <p class="theme-desc">{{ theme.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 会议日程简要 -->
    <section class="section schedule-section">
      <div class="container">
        <h2 class="section-title">大会日程</h2>
        <p class="section-subtitle">两天精彩议程，汇聚国内外顶尖科学家</p>
        <div class="schedule-tabs">
          <div 
            v-for="(day, index) in scheduleDays" 
            :key="index"
            class="schedule-tab"
            :class="{ 'active': activeDay === index }"
            @click="activeDay = index"
          >
            <span class="tab-day">第{{ index + 1 }}天</span>
            <span class="tab-date">{{ day.date }}</span>
          </div>
        </div>
        <div class="schedule-content">
          <div class="schedule-timeline">
            <div 
              v-for="(item, idx) in currentDaySchedule" 
              :key="idx" 
              class="timeline-item"
              :class="{ 'highlight': item.type === 'keynote' }"
            >
              <div class="timeline-time">{{ item.time }}</div>
              <div class="timeline-dot"></div>
              <div class="timeline-content">
                <h4 class="timeline-title">{{ item.title }}</h4>
                <p v-if="item.speaker" class="timeline-speaker">
                  <i class="el-icon-user"></i> {{ item.speaker }}
                </p>
                <p v-if="item.location" class="timeline-location">
                  <i class="el-icon-location-outline"></i> {{ item.location }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="schedule-more">
          <el-button type="primary" plain @click="$router.push('/schedule')">
            查看完整日程 <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
      </div>
    </section>

    <!-- 报名参会 -->
    <section class="section registration-section">
      <div class="container">
        <h2 class="section-title">报名参会</h2>
        <p class="section-subtitle">注册成为参会者，开启天文探索之旅</p>
        <div class="registration-info">
          <div class="reg-info-card">
            <div class="reg-icon">
              <i class="el-icon-user"></i>
            </div>
            <h3>第一步：注册账号</h3>
            <p>填写个人信息完成参会注册</p>
          </div>
          <div class="reg-arrow">
            <i class="el-icon-arrow-right"></i>
          </div>
          <div class="reg-info-card">
            <div class="reg-icon">
              <i class="el-icon-document-checked"></i>
            </div>
            <h3>第二步：缴纳费用</h3>
            <p>注册成功后在个人中心完成缴费</p>
          </div>
          <div class="reg-arrow">
            <i class="el-icon-arrow-right"></i>
          </div>
          <div class="reg-info-card">
            <div class="reg-icon">
              <i class="el-icon-success"></i>
            </div>
            <h3>第三步：参会成功</h3>
            <p>获得参会资格与会议资料</p>
          </div>
        </div>

        <div class="reg-fee-info" v-if="conferenceInfo">
          <div class="fee-item">
            <span class="fee-label">学生注册费</span>
            <div class="fee-details">
              <div class="fee-price" v-if="conferenceInfo.early_bird_fee_student">
                <span class="price-type">线上注册费：</span>
                <span class="price-value">¥{{ conferenceInfo.early_bird_fee_student }}</span>
              </div>
              <div class="fee-price" v-if="conferenceInfo.fee_student">
                <span class="price-type">现场注册费：</span>
                <span class="price-value">¥{{ conferenceInfo.fee_student }}</span>
              </div>
              <div class="fee-price" v-if="!conferenceInfo.early_bird_fee_student && !conferenceInfo.fee_student">
                <span class="price-value">¥2000</span>
              </div>
            </div>
          </div>
          <div class="fee-item">
            <span class="fee-label">注册费</span>
            <div class="fee-details">
              <div class="fee-price" v-if="conferenceInfo.early_bird_fee">
                <span class="price-type">线上注册费：</span>
                <span class="price-value">¥{{ conferenceInfo.early_bird_fee }}</span>
              </div>
              <div class="fee-price" v-if="conferenceInfo.fee">
                <span class="price-type">现场注册费：</span>
                <span class="price-value">¥{{ conferenceInfo.fee }}</span>
              </div>
              <div class="fee-price" v-if="!conferenceInfo.early_bird_fee && !conferenceInfo.fee">
                <span class="price-value">¥2500</span>
              </div>
            </div>
          </div>
          <!-- 线上注册费截止日期 -->
          <div class="early-bird-info" v-if="conferenceInfo.early_bird_deadline">
            <span class="early-bird-label">线上注册截止：</span>
            <span class="early-bird-date">{{ conferenceInfo.early_bird_deadline }}</span>
          </div>
        </div>

        <div class="reg-action">
          <el-button type="primary" size="large" @click="goToRegister">
            {{ isLoggedIn ? '进入个人中心' : '立即注册' }} <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
      </div>
    </section>

    <!-- 主办单位 -->
    <section class="section organizer-section">
      <div class="container">
        <h2 class="section-title">组织机构</h2>
        <div class="organizer-list">
          <div class="organizer-item">
            <h4>主办单位</h4>
            <p>{{ conferenceInfo.organizer }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { conferenceApi, scheduleApi } from '@/api'

export default {
  name: 'Home',
  data() {
    return {
      activeDay: 0,
      currentBannerIndex: 0,
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      conferenceInfo: null,
      banners: [
        { id: 1, image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920', title: '探索宇宙奥秘' },
        { id: 2, image: 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1920', title: '星空下的思考' },
        { id: 3, image: 'https://images.unsplash.com/photo-1444703686981-a3abbc4d4fe3?w=1920', title: '天文学前沿' }
      ],
      themes: [
        { icon: 'el-icon-s-opportunity', title: '银河系与恒星', desc: '银河系形成演化、恒星物理、系外行星科学' },
        { icon: 'el-icon-moon', title: '星系和宇宙学', desc: '星系天文学、宇宙大尺度结构、暗物质暗能量、引力波' },
        { icon: 'el-icon-star-off', title: '天文仪器与巡天', desc: '天文大装置、巡天设备与项目、实验室天体物理' },
        // { icon: 'el-icon-sunrise', title: '恒星物理', desc: '恒星形成、演化与终结的全生命周期研究' },
        // { icon: 'el-icon-place', title: '银河系动力学', desc: '银河系结构、暗物质分布与动力学研究' },
        // { icon: 'el-icon-data-analysis', title: '天文大数据与AI', desc: '人工智能在天文数据处理中的创新应用' }
      ],
      scheduleDays: [],
      scheduleData: [],
      countdownTimer: null,
      targetDate: null
    }
  },
  computed: {
    currentDaySchedule() {
      return this.scheduleData[this.activeDay] || []
    },
    isLoggedIn() {
      return !!localStorage.getItem('token')
    },

  },
  mounted() {
    this.fetchConferenceInfo()
    this.fetchSchedule()
    this.fetchBanners()
    this.startCountdown()
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    async fetchConferenceInfo() {
      try {
        const res = await conferenceApi.getDetail(1)
        if (res.data) {
          this.conferenceInfo = res.data
          // 如果后端返回了开始时间，使用后端的时间
          if (res.data.start_date) {
            // 使用会议开始日期，如果包含时间信息则使用完整时间，否则默认当天8点
            const startDate = res.data.start_date
            let targetDateTime
            if (startDate.includes('T')) {
              targetDateTime = new Date(startDate)
            } else {
              // 如果只有日期部分，设置为当天的8点
              targetDateTime = new Date(`${startDate}T08:00:00`)
            }
            this.targetDate = targetDateTime.getTime()
          } else {
            // 如果没有返回开始时间，使用默认时间
            this.targetDate = new Date('2026-08-07T08:00:00').getTime()
          }
        } else {
          // 如果没有获取到数据，使用默认时间
          this.targetDate = new Date('2026-08-07T08:00:00').getTime()
        }
      } catch (error) {
        console.error('获取会议信息失败:', error)
        // 出错时使用默认时间
        this.targetDate = new Date('2026-08-07T08:00:00').getTime()
      }
    },
    async fetchSchedule() {
      try {
        const res = await scheduleApi.getList(1)
        if (res.data && res.data.length > 0) {
          // 数据已按天分组，每天有schedules数组
          this.scheduleDays = res.data.map(dayData => ({
            date: this.formatDate(dayData.date)
          }))
          this.scheduleData = res.data.map(dayData => {
            return (dayData.schedules || []).map(item => ({
              time: `${this.formatTime(item.start_time)}-${this.formatTime(item.end_time)}`,
              title: item.title,
              speaker: item.speaker || '',
              location: item.location || '',
              type: item.type || 'normal'
            }))
          })
        }
      } catch (error) {
        console.log('使用默认日程数据')
        // 使用默认数据
        this.scheduleDays = []
        this.scheduleData = []
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const month = date.getMonth() + 1
      const day = date.getDate()
      const weekDay = weekDays[date.getDay()]
      return `${month}月${day}日（${weekDay}）`
    },
    formatTime(timeStr) {
      if (!timeStr) return ''
      return timeStr.substring(0, 5)
    },
    startCountdown() {
      const updateCountdown = () => {
        // 确保targetDate存在
        if (!this.targetDate) {
          this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 }
          return
        }
        
        const now = new Date().getTime()
        const distance = this.targetDate - now

        if (distance < 0) {
          this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 }
          return
        }

        this.countdown = {
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        }
      }

      updateCountdown()
      this.countdownTimer = setInterval(updateCountdown, 1000)
    },
    goToRegister() {
      if (this.isLoggedIn) {
        this.$router.push('/user')
      } else {
        this.$router.push('/register')
      }
    },
    goToAbout() {
      this.$router.push('/about')
    },
    goToPay(type) {
      const token = localStorage.getItem('token')
      if (token) {
        this.$router.push({ path: '/pay', query: { type } })
      } else {
        this.$router.push({ path: '/login', query: { redirect: `/pay?type=${type}` } })
      }
    },
    onBannerChange(index) {
      this.currentBannerIndex = index
    },
    async fetchBanners() {
      try {
        const res = await conferenceApi.getBanners(1)
        if (res.data && res.data.length > 0) {
          this.banners = res.data
        }
      } catch (error) {
        console.log('使用默认Banner')
      }
    },
    getImageUrl(imageUrl) {
      if (!imageUrl) return ''
      // 如果已经是完整的 URL（以 http:// 或 https:// 开头），直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }
      // 否则加上域名前缀
      return `http://meeting.nmgqyc.com${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`
    }
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  padding-top: 0;
}

// Hero Banner
.hero-banner {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  margin-top: -70px;
}

.banner-carousel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ::v-deep .el-carousel__container {
    height: 100vh !important;
  }

  ::v-deep .el-carousel__arrow {
    width: 50px;
    height: 50px;
    font-size: 24px;
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    transition: all 0.3s;
    z-index: 100;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  ::v-deep .el-carousel__arrow--left {
    left: 40px;
  }

  ::v-deep .el-carousel__arrow--right {
    right: 40px;
  }
}

.banner-slide {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  position: relative;
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(13, 22, 66, 0.2) 0%,
    rgba(26, 35, 126, 0.2) 50%,
    rgba(57, 73, 171, 0.2) 100%
  );
}

.hero-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 120px 60px 80px;
}

.content-wrapper {
  width: 100%;
  max-width: 1400px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 60px;
}

.hero-left {
  flex: 1;
  color: #fff;
}

.hero-badge {
  display: inline-block;
  padding: 10px 28px;
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid #ffd700;
  border-radius: 25px;
  color: #ffd700;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 28px;
  backdrop-filter: blur(10px);
}

.hero-title {
  font-size: 52px;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.4);
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 26px;
  opacity: 0.95;
  margin-bottom: 12px;
  letter-spacing: 3px;
  font-weight: 300;
}

.hero-desc {
  font-size: 18px;
  opacity: 0.8;
  margin-bottom: 36px;
}

.hero-info {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 40px;

  .info-item {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 16px;
    opacity: 0.9;

    i {
      font-size: 20px;
      color: #ffd700;
    }
  }
}

.hero-actions {
  display: flex;
  gap: 20px;

  .el-button {
    padding: 16px 44px;
    font-size: 16px;
    border-radius: 30px;

    &--primary {
      background: linear-gradient(135deg, #ffd700 0%, #ffec8b 100%);
      border: none;
      color: #1a237e;
      font-weight: 600;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 10px 30px rgba(255, 215, 0, 0.4);
      }
    }

    &.is-plain {
      background: rgba(255, 255, 255, 0.1);
      border: 2px solid rgba(255, 255, 255, 0.5);
      color: #fff;
      backdrop-filter: blur(10px);

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        border-color: #fff;
      }
    }
  }
}

.hero-right {
  flex-shrink: 0;
}

.countdown-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 40px 50px;
  text-align: center;
  color: #fff;

  .countdown-title {
    font-size: 18px;
    margin-bottom: 24px;
    opacity: 0.9;
    letter-spacing: 2px;
  }

  .countdown-timer {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .countdown-sep {
    font-size: 36px;
    color: #ffd700;
    font-weight: 300;
    margin-top: -20px;
  }

  .countdown-item {
    text-align: center;
    min-width: 70px;

    .countdown-value {
      display: block;
      font-size: 48px;
      font-weight: 700;
      color: #ffd700;
      line-height: 1;
      text-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
    }

    .countdown-unit {
      font-size: 13px;
      opacity: 0.7;
      margin-top: 10px;
      display: block;
    }
  }
}

.banner-indicators {
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 20;

  .indicator-dot {
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.4);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: rgba(255, 255, 255, 0.6);
    }

    &.active {
      width: 36px;
      border-radius: 6px;
      background: #ffd700;
    }
  }
}

.hero-scroll-hint {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: bounce 2s infinite;
  z-index: 20;

  i {
    margin-top: 8px;
    font-size: 20px;
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
  40% { transform: translateX(-50%) translateY(-10px); }
  60% { transform: translateX(-50%) translateY(-5px); }
}

// Section通用样式
.section {
  padding: 80px 0;

  &:nth-child(odd) {
    background: #f8f9fc;
  }
}

.section-title {
  font-size: 36px;
  font-weight: 700;
  color: #1a237e;
  text-align: center;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 16px;
  color: #666;
  text-align: center;
  margin-bottom: 50px;
}

// 主题区域
.theme-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.theme-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(26, 35, 126, 0.15);

    .theme-icon {
      background: linear-gradient(135deg, #1a237e 0%, #7c4dff 100%);
      color: #fff;
    }
  }

  .theme-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    background: #f0f2f8;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    i {
      font-size: 36px;
      color: #1a237e;
    }
  }

  .theme-title {
    font-size: 20px;
    font-weight: 600;
    color: #1a237e;
    margin-bottom: 12px;
  }

  .theme-desc {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
  }
}

// 日程区域
.schedule-section {
  background: linear-gradient(180deg, #0d1642 0%, #1a237e 100%) !important;
  color: #fff;

  .section-title {
    color: #fff;
  }

  .section-subtitle {
    color: rgba(255, 255, 255, 0.7);
  }
}

.schedule-tabs {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
}

.schedule-tab {
  padding: 16px 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &.active {
    background: linear-gradient(135deg, #ffd700 0%, #ffec8b 100%);
    color: #1a237e;
  }

  .tab-day {
    display: block;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .tab-date {
    font-size: 14px;
    opacity: 0.8;
  }
}

.schedule-timeline {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  padding-left: 120px;

  &::before {
    content: '';
    position: absolute;
    left: 110px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: rgba(255, 255, 255, 0.2);
  }
}

.timeline-item {
  position: relative;
  padding: 20px 0 20px 40px;
  
  &.highlight {
    .timeline-content {
      background: rgba(255, 215, 0, 0.15);
      border-left: 3px solid #ffd700;
    }

    .timeline-dot {
      background: #ffd700;
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
    }
  }
}

.timeline-time {
  position: absolute;
  left: -120px;
  top: 24px;
  width: 100px;
  text-align: right;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.timeline-dot {
  position: absolute;
  left: -6px;
  top: 28px;
  width: 12px;
  height: 12px;
  background: #7c4dff;
  border-radius: 50%;
}

.timeline-content {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}

.timeline-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.timeline-speaker, .timeline-location {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 6px;

  i {
    margin-right: 6px;
    color: #ffd700;
  }
}

.schedule-more {
  text-align: center;
  margin-top: 40px;

  .el-button {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    color: #fff;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #fff;
    }
  }
}

// 报名区域
.registration-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-bottom: 50px;
}

.reg-info-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 240px;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(26, 35, 126, 0.15);
  }

  .reg-icon {
    width: 70px;
    height: 70px;
    margin: 0 auto 20px;
    background: linear-gradient(135deg, #1a237e 0%, #7c4dff 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 32px;
      color: #fff;
    }
  }

  h3 {
    font-size: 18px;
    color: #1a237e;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    color: #666;
  }
}

.reg-arrow {
  i {
    font-size: 28px;
    color: #1a237e;
  }
}

.reg-fee-info {
  display: flex;
  justify-content: center;
  gap: 60px;
  margin-bottom: 40px;
  padding: 30px 0;
  background: rgba(26, 35, 126, 0.03);
  border-radius: 12px;
}

.fee-item {
  text-align: center;

  .fee-label {
    display: block;
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }

  .fee-details {
    margin-top: 8px;
  }

  .fee-price {
    display: flex;
    align-items: center;
    font-size: 16px;
    margin-bottom: 6px;

    .price-type {
      color: #666;
      font-size: 14px;
    }

    .price-value {
      font-size: 20px;
      font-weight: 700;
      color: #f56c6c;
      margin-left: 4px;
    }
  }

  .early-bird-info {
    grid-column: 1 / -1;
    text-align: center;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px dashed #e0e0e0;
    font-size: 14px;
    color: #999;

    .early-bird-date {
      color: #f56c6c;
      font-weight: 600;
    }
  }
}

.reg-action {
  text-align: center;

  .el-button {
    padding: 16px 60px;
    font-size: 18px;
    border-radius: 30px;
    background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
    border: none;

    &:hover {
      background: linear-gradient(135deg, #3949ab 0%, #7c4dff 100%);
      transform: translateY(-3px);
      box-shadow: 0 10px 30px rgba(26, 35, 126, 0.3);
    }
  }
}

// 组织机构
.organizer-list {
  display: flex;
  justify-content: center;
  gap: 60px;
}

.organizer-item {
  text-align: center;

  h4 {
    font-size: 14px;
    color: #999;
    margin-bottom: 12px;
    font-weight: 400;
  }

  p {
    font-size: 18px;
    color: #1a237e;
    font-weight: 600;
  }
}

// 响应式
@media screen and (max-width: 1200px) {
  .content-wrapper {
    flex-direction: column;
    text-align: center;
  }

  .hero-left {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .hero-info {
    align-items: center;
  }

  .hero-actions {
    justify-content: center;
  }

  .hero-right {
    margin-top: 20px;
  }

  .countdown-card {
    padding: 30px 40px;
  }
}

@media screen and (max-width: 992px) {
  .hero-title {
    font-size: 40px;
  }

  .hero-subtitle {
    font-size: 20px;
  }

  .hero-content {
    padding: 100px 30px 80px;
  }

  .banner-carousel {
    ::v-deep .el-carousel__arrow {
      display: none;
    }
  }

  .theme-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .registration-info {
    flex-wrap: wrap;
  }

  .reg-arrow {
    display: none;
  }

  .reg-fee-info {
    flex-direction: column;
    gap: 20px;
  }
}

@media screen and (max-width: 576px) {
  .hero-content {
    padding: 100px 20px 60px;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .hero-desc {
    font-size: 14px;
  }

  .hero-info {
    .info-item {
      font-size: 14px;
    }
  }

  .hero-actions {
    flex-direction: column;
    gap: 12px;
    width: 100%;

    .el-button {
      width: 100%;
      padding: 14px 30px;
    }
  }

  .countdown-card {
    padding: 25px 20px;

    .countdown-timer {
      gap: 6px;
    }

    .countdown-item {
      min-width: 50px;

      .countdown-value {
        font-size: 32px;
      }
    }

    .countdown-sep {
      font-size: 24px;
    }
  }

  .banner-indicators {
    bottom: 80px;
  }

  .section {
    padding: 50px 0;
  }

  .section-title {
    font-size: 28px;
  }

  .theme-grid {
    grid-template-columns: 1fr;
  }

  .schedule-timeline {
    padding-left: 30px;

    &::before {
      left: 20px;
    }
  }

  .timeline-time {
    position: static;
    width: auto;
    text-align: left;
    margin-bottom: 8px;
  }

  .timeline-dot {
    left: -16px;
  }

  .organizer-list {
    flex-direction: column;
    gap: 30px;
  }
}
</style>
